import type {
  TError,
  TReviewDueState,
  TReviewFilterMode,
  TReviewMissingDateField,
  TReviewMissingFacetsResponse,
  TReviewMissingProgress,
  TReviewMissingResponse,
} from '@onesource/schemas';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import axios from '../../axios';
import { getReviewQueryKey, toReviewSearchParams, type TReviewFilterParams } from './use-review-queue';

export interface TReviewMissingParams extends TReviewFilterParams {
  progress: TReviewMissingProgress[];
  progressMode: TReviewFilterMode;
  due: TReviewDueState[];
  dueMode: TReviewFilterMode;
  dateField: TReviewMissingDateField;
  page?: number;
  perPage?: number;
}

/** Students with Hands-On Practice forms not submitted, in a Teacher Review sub-space. */
export const useGetReviewMissing = (spaceId: string, params: TReviewMissingParams, enabled: boolean) =>
  useQuery<TReviewMissingResponse, TError>({
    queryKey: [...getReviewQueryKey(spaceId), 'missing', toReviewSearchParams(params)],
    queryFn: async () => {
      const res = await axios.get<TReviewMissingResponse>(`/community-hub/spaces/${spaceId}/review/missing`, {
        params: toReviewSearchParams(params),
      });
      return res.data;
    },
    enabled: !!spaceId && enabled,
    placeholderData: keepPreviousData,
  });

/** The Not submitted filter values, each counted in students. Only fetched while a filter panel is open. */
export const useGetReviewMissingFacets = (spaceId: string, params: TReviewMissingParams, enabled: boolean) => {
  const { page: _page, perPage: _perPage, ...filters } = params;
  return useQuery<TReviewMissingFacetsResponse, TError>({
    queryKey: [...getReviewQueryKey(spaceId), 'missing-facets', toReviewSearchParams(filters)],
    queryFn: async () => {
      const res = await axios.get<TReviewMissingFacetsResponse>(
        `/community-hub/spaces/${spaceId}/review/missing/facets`,
        { params: toReviewSearchParams(filters) },
      );
      return res.data;
    },
    enabled: !!spaceId && enabled,
    placeholderData: keepPreviousData,
  });
};
